import { Text, Flex, Button } from "@chakra-ui/react";
import { ShareIcon } from "../../../components/icons/ShareIcon";
import { BackButton } from "../../../components/buttons/BackButton";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { Section, NavigationSource } from "../../../types";
import { useIsSmallBreakpoint, useMobileBreakpoint } from "../../../utils/useBreakpoints";
import { useWasSameOrigin } from "../../../hooks/useWasSameOrigin";

interface ExpandedContentHeaderProps {
  section: Section;
  data: any[];
  navigationSource?: NavigationSource | null;
}

export function ExpandedContentHeader({
  section,
  data,
  navigationSource,
}: ExpandedContentHeaderProps) {
  const navigate = useNavigate();
  const isSmall = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();
  const wasSameOrigin = useWasSameOrigin();

  const share = () => {
    const shareData = {
      title: section?.display_name,
      text: section?.description,
      url: section?.share_url,
    };
    navigator.share(shareData);
  };

  return (
    <Flex
      width="100%"
      alignItems="center"
      position="sticky"
      top="0"
      zIndex={1}
      background="charcoal.charcoal"
      pb="16px"
    >
      <Flex
        position="relative"
        direction="column"
        gap="8px"
        width="100%"
        pt={isMobile ? 6 : 10}
        pl={isSmall ? 4 : 0}
        pr={isSmall ? 4 : 0}
        pb={4}
      >
        <BackButton
          text={navigationSource?.sourceChannelName
            ? `Back to ${navigationSource.sourceChannelName}`
            : t("components.cards.expandedContentSection.backToHome")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Use browser back for scroll restoration when there's in-app history
            // Fall back to home for direct links, new tabs, or refreshes
            if (wasSameOrigin) {
              navigate(-1);
            } else {
              navigate("/");
            }
          }}
        />
        <Flex alignItems="center" width="100%" justifyContent="space-between">
          <Text
            variant="title"
            fontSize={isMobile ? "20px" : "38px"}
            lineHeight="normal"
            letterSpacing=".5px"
          >
            {section?.display_name}
          </Text>
          {!isSmall && (
            <Button
              variant="tertiary"
              gap="8px"
              alignItems="center"
              onClick={share}
              aria-label={t("components.cards.expandedContentSection.share")}
            >
              <ShareIcon w="16px" h="16px" />{" "}
              {t("components.cards.expandedContentSection.share")}
            </Button>
          )}
        </Flex>
        <Flex gap="8px" flexWrap="wrap">
          {section?.description ? (
            <>
              <Text variant="subtitle" fontSize="16px">
                {section?.description}
              </Text>
              <Text variant="subtitle" fontSize="16px">
                &#183;
              </Text>
            </>
          ) : null}
          {data.length && !section?.meta?.layout?.hide_item_count ? (
            <Text variant="subtitle" fontSize="16px">
              {data.length} {section?.meta.layout.style.context}
            </Text>
          ) : null}
        </Flex>
      </Flex>
      {isSmall && (
        <Button
          variant="iconOnly"
          onClick={share}
          aria-label={t("components.cards.expandedContentSection.share")}
          gap="8px"
          alignItems="center"
          color="dune.dune"
          position="absolute"
          top="20px"
          right="20px"
        >
          <ShareIcon
            transition="all .1s linear"
            w="22px"
            h="22px"
            _hover={{ color: "blaze.blaze" }}
          />
        </Button>
      )}
    </Flex>
  );
}
